import React, { useState, useEffect, useCallback, useMemo } from "react";

import { isEmpty, isEqual, pick } from "lodash";
import { useWatch } from "react-hook-form-7";
import { useDebounce } from "react-use";

import { Stack, Text, Spinner } from "@chakra-ui/react";

import useStore from "../../../../store";
import useTranslations from "../../../../hooks/useTranslations";

import useDraftAnswer from "../../hooks/useDraftAnswer";

import {
  coerceToAnswerValue,
  getAnswerTypeFromQuestionType,
} from "../../utils/AnswerValueHelpers";

import {
  AnswerFragment,
  InsertAnswerInput,
  useInsertAnswer,
} from "../../../../graphql/generated/definitions";
import { QuestionProps } from "../questions/types/QuestionProps";

const AnswerAutoSave: React.FC<QuestionProps> = ({
  question,
  report,
  control,
  path,
  setValue,
}) => {
  const t = useTranslations();
  const viewer = useStore((state) => state.viewer);
  const draftAnswer = useDraftAnswer(question);

  // Store the current draft answer if any otherwise null
  const [answer, setAnswer] = useState<AnswerFragment | null>(
    draftAnswer || null
  );

  // Ensure the answerId is properly in sync
  useEffect(() => {
    if (answer) setValue(`${path}.id`, answer.id);
  }, [setValue, answer]);

  // Watch every value that can trigger an autosave
  const [fieldValue, fieldTimeSpentValue, fieldCommentValue] = useWatch({
    name: [`${path}.value`, `${path}.timeSpent`, `${path}.comment`],
    control,
  });

  // Ensure that the answer will pass validation before even trying to send it
  const valueBlocked = isEmpty(fieldValue);
  const timeSpentBlocked = question.timeTracked && isEmpty(fieldTimeSpentValue);
  const isReadyToBeSent = !valueBlocked && !timeSpentBlocked;

  // Compute the new answer
  const answerMutationInput: InsertAnswerInput = useMemo(
    () => ({
      userId: viewer.id,
      collaboratorId: report?.review?.collaboratorId,

      id: answer?.id,
      questionId: question.id,
      reportId: report.id,

      value: coerceToAnswerValue({
        type: getAnswerTypeFromQuestionType(question.type),
        value: fieldValue,
        comment: fieldCommentValue,
      }),
      timeSpent: isEmpty(fieldTimeSpentValue)
        ? null
        : parseInt(fieldTimeSpentValue, 10),
      type: getAnswerTypeFromQuestionType(question.type),
      isDraft: true,
    }),
    [
      fieldValue,
      fieldCommentValue,
      fieldTimeSpentValue,
      answer?.id,
      report?.review?.collaboratorId,
    ]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  // Exclude collaboratorId from the payload to match against the
  // already saved answer as it is not part of an AnswerFragment object
  const { collaboratorId: _, ...matchableAnswerFields } = answerMutationInput;
  // Check if the answer is the same one as the one already saved
  const isInSync = isEqual(
    matchableAnswerFields,
    pick(answer, Object.keys(matchableAnswerFields))
  );

  /**
   * **THIS IS THE IMPORTANT ONE**
   *
   * An answer should be updated if:
   * - The answer is in a valid state:
   *
   *  `isReadyToBeSent && !isInSync`
   * *OR*
   * - The viewer is erasing a precedent answer
   *
   *  `!isReadyToBeSent && !isInSync && answer !== null`
   */
  const shouldUpdate =
    (isReadyToBeSent && !isInSync) ||
    (!isReadyToBeSent && !isInSync && answer !== null);

  const setQuestionSyncStatus = useStore(
    useCallback((state) => state.report.setQuestionSyncStatus, [])
  );
  useEffect(() => {
    setQuestionSyncStatus(question.id, !shouldUpdate);
  }, [shouldUpdate, question]);

  // GraphQL mutation
  const [insertAnswer, { loading: answerLoading, error: answerError }] =
    useInsertAnswer();

  // Debounced input change listener
  useDebounce(
    async () => {
      // Mutate only if the input should update (see above for the actual condition)
      if (shouldUpdate) {
        try {
          const answerResult = await insertAnswer({
            variables: { input: answerMutationInput },
          });

          setAnswer(answerResult?.data?.insertAnswer.answer);
        } catch {
          console.error("Fail to save current answer");
        }
      }
    },
    1000,
    [answerMutationInput, shouldUpdate]
  );

  const isAnswerSaved = !answerLoading && !answerError && answer?.id;

  // If the answer is beeing updated
  if (shouldUpdate) {
    return (
      <Stack
        align="center"
        direction="row"
        spacing="2"
        pt="2"
        sx={{ marginTop: "0px !important" }}
      >
        <Spinner color="gray.500" size="xs" />
        <Text color="gray.500" fontSize="sm">
          {t("reports.edit.saving")}
        </Text>
      </Stack>
    );
  }

  // If the answer is valid & registered
  if (isReadyToBeSent && isAnswerSaved) {
    return (
      <Text
        color="green.500"
        fontSize="sm"
        pt="2"
        sx={{ marginTop: "0px !important" }}
      >
        {t("reports.edit.saved")}
      </Text>
    );
  }

  // At this point the answer must be either in an invalid state or missing
  return null;
};

export default React.memo(AnswerAutoSave);
